import { configureStore, ReducersMapObject } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

import { ordersRecognitionSidebarReducer } from '@entities/sidebar/slice/orders-recognition-sidebar.slice';
import { widgetModalsReducer } from '@features/WidgetModalController/model/widgetModalsSlice';
import { addModalReducer } from 'widgets/Modals/model/slice/addModalSlice';
import { editModalReducer } from 'widgets/Modals/model/slice/editModalSlice';

import { StateSchema } from './config/StateSchema';
import board from './slices/boardSlice';
import { dashboardReducer } from './slices/dashboardSlice/dashboardSlice';
import {
    estimateUndoActions,
    estimateUndoReducer,
} from './slices/estimateUndoSlice/estimateUndoSlice';
import { filesReducer } from './slices/filesSlice/filesSlice';
import filter from './slices/filterSlice';
import inventory from './slices/inventorySlice';
import { measurementsReducer } from './slices/measurementsSlice/measurementsSlice';
import moveInventoryQuantityModal from './slices/moveInventoryQuantityModalSlice';
import { notificationReducer } from './slices/notificationSlice/notificationSlice';
import { paymentReducer } from './slices/paymentSlice/paymentSlice';
import restrictions from './slices/restrictionsSlice';
import { taskBoardReducer } from './slices/taskBoardSlice/taskBoardSlice';
import toast from './slices/toastSlice';
import totalInfo from './slices/totalInfoSlice';
import warehouse from './slices/warehouseSlice';
import warehouseTotalInfo from './slices/warehouseTotalInfoSlice';
import { tasksInformationReducer } from '../entities/task/model/slice/tasksInformation';
import { tasksReducer } from '../entities/task/model/slice/taskSlice';
import { lightboxSliceReducer } from '../shared/ui/Lightbox/model/lightboxSlice';
import { charterReducer } from '../TemporaryConmonents/Gant/store/reducers/charterSlice/charterSlice';
import { connectionReducer } from '../TemporaryConmonents/Gant/store/reducers/connectionSlice/connectionSlice';
import { gantReducer } from '../TemporaryConmonents/Gant/store/reducers/gantSlice/gantSlice';
import { positionsConnectionsReducer } from '../TemporaryConmonents/Gant/store/reducers/positionsConnections/positionsConnectionsSlice';
import { positionReducer } from '../TemporaryConmonents/Gant/store/reducers/positionSlice/positionSlice';
import { stageReducer } from '../TemporaryConmonents/Gant/store/reducers/stageSlice/stageSlice';
import { tableWidthReducer } from '../TemporaryConmonents/Gant/store/reducers/tableWidthSlice/tableWidthSlice';

const ignoreActions = ['measurements/changeUploadImage'];
const ignorePaths = ['measurementsReducer.imageList.uploadImages.*.file'];

const rootReducers: ReducersMapObject<StateSchema> = {
    board,
    inventory,
    filter,
    warehouse,
    restrictions,
    totalInfo,
    warehouseTotalInfo,
    toast,
    moveInventoryQuantityModal,
    paymentReducer,
    stageReducer,
    charterReducer,
    positionReducer,
    positionsConnectionsReducer,
    tableWidthReducer,
    connectionReducer,
    gantReducer,
    filesReducer,
    measurementsReducer,
    dashboardReducer,
    notificationReducer,
    taskBoardReducer,
    tasksInformationReducer,
    tasksReducer,
    addModalReducer,
    editModalReducer,
    estimateUndoReducer,
    lightboxSliceReducer,
    ordersRecognitionSidebarReducer,
    widgetModalsReducer,
};

const store = configureStore({
    reducer: rootReducers,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [...ignoreActions],
                ignoredPaths: [...ignorePaths],
            },
        }),
});

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export type RootState = ReturnType<typeof store.getState>;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export default store;

window.MyApp = {
    store,
    actions: {
        estimateUndo: estimateUndoActions,
    },
};
